import React from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { Text } from '@/components/common/text';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { AvatarCircle } from '@/components';
import { SVG_COMPONENTS } from '@/assets/admin';
import { IConversation, IConversationAccount } from '@/types/admin/messages';
import { ResidentDetailsSidePanel } from '@/components/admin/resident/desktop/ResidentDetailsSidePanel';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';

interface ConversationDetailsPanelProps {
  conversation: IConversation;
  onClose: () => void;
}

export const ConversationDetailsPanel: React.FC<ConversationDetailsPanelProps> = ({ conversation, onClose }) => {
  const { redesign } = useAdminColors();
  const participants: IConversationAccount[] = (conversation?.conversationAccounts || []).filter((acct: any) => acct.type === 'resident');
  const threadOwners: IConversationAccount[] = (conversation?.conversationAccounts || []).filter((acct: any) => acct.type === 'admin');

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Local state for resident drilldown side panel
  const [showResidentPanel, setShowResidentPanel] = React.useState(false);
  const [residentParams, setResidentParams] = React.useState<{ accountId: number | null; leaseId: number | null }>({ accountId: null, leaseId: null });

  const openResidentPanel = (participant: IConversationAccount) => {
    const assignee = participant.assigneeAccount;
    const leaseId = assignee?.leaseCustomers?.[0]?.lease?.id || null;
    const accountId = assignee?.id || null;
    if (accountId && leaseId) {
      setResidentParams({ accountId, leaseId });
      setShowResidentPanel(true);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: redesign.bg.base }]}>
      <DesktopPanelHeader title="Conversation Details" onClose={onClose} />

      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 16 }}>
        {/* Conversation info */}
        <View style={{ paddingHorizontal: 16, paddingTop: 16 }}>
          <GlassCard padding={16}>
            <Text style={{ fontSize: 16, fontWeight: '600', color: redesign.text.primary, marginBottom: 6 }} numberOfLines={2}>{conversation.subject}</Text>
            <Text style={{ fontSize: 14, color: redesign.text.muted }}>Created {formatDate(conversation.createdAt)}</Text>
          </GlassCard>
        </View>

        {/* Participants */}
        <View style={{ paddingHorizontal: 16, paddingTop: 20 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text style={{ fontSize: 18, fontWeight: '600', color: redesign.text.primary }}>Participants</Text>
            <Text style={{ fontSize: 16, color: redesign.text.muted }}>{participants.length}</Text>
          </View>

          {participants.length === 0 ? (
            <View style={{ alignItems: 'center', paddingVertical: 24 }}>
              <SVG_COMPONENTS.GROUP width={32} height={32} color={redesign.text.muted} />
              <Text style={{ fontSize: 14, color: redesign.text.muted, marginTop: 8 }}>No participants</Text>
            </View>
          ) : (
            participants.map((participant) => {
              const nameString = participant.assigneeAccount.firstName && participant.assigneeAccount.lastName
                ? `${participant.assigneeAccount.firstName} ${participant.assigneeAccount.lastName}`
                : participant.assigneeAccount.email;
              const assigneeLC = participant.assigneeAccount.leaseCustomers?.[0];
              const leaseStatus = assigneeLC?.lease?.statusType;
              const hasLoggedIn = Boolean(participant.assigneeAccount.lastInteractionDate);
              return (
                <TouchableOpacity
                  key={participant.id}
                  activeOpacity={0.8}
                  onPress={() => openResidentPanel(participant)}
                  style={{ marginBottom: 8 }}
                >
                  <GlassCard padding={12}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <AvatarCircle firstName={participant.assigneeAccount.firstName || ''} lastName={participant.assigneeAccount.lastName || ''} size={40} style={{ marginRight: 12 }} />
                      <View style={{ flex: 1 }}>
                        <Text style={{ fontSize: 16, fontWeight: '600', color: redesign.text.primary, marginBottom: 2 }}>{nameString}</Text>
                        {leaseStatus ? (
                          <Text style={{ fontSize: 14, color: redesign.text.muted }}>{leaseStatus} resident</Text>
                        ) : (
                          !hasLoggedIn && <Text style={{ fontSize: 12, color: redesign.text.subtle, fontStyle: 'italic' }}>Has not created a Resio account</Text>
                        )}
                      </View>
                    </View>
                  </GlassCard>
                </TouchableOpacity>
              );
            })
          )}
        </View>

        {/* Thread Owners */}
        <View style={{ paddingHorizontal: 16, paddingTop: 20 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text style={{ fontSize: 18, fontWeight: '600', color: redesign.text.primary }}>Thread Owners</Text>
            <Text style={{ fontSize: 16, color: redesign.text.muted }}>{threadOwners.length}</Text>
          </View>

          {threadOwners.length === 0 ? (
            <View style={{ alignItems: 'center', paddingVertical: 24 }}>
              <SVG_COMPONENTS.ADMIN width={32} height={32} color={redesign.text.muted} />
              <Text style={{ fontSize: 14, color: redesign.text.muted, marginTop: 8 }}>No thread owners assigned</Text>
            </View>
          ) : (
            threadOwners.map((owner) => {
              const nameString = owner.assigneeAccount.firstName && owner.assigneeAccount.lastName
                ? `${owner.assigneeAccount.firstName} ${owner.assigneeAccount.lastName}`
                : owner.assigneeAccount.email;
              return (
                <View key={owner.id} style={{ marginBottom: 8 }}>
                  <GlassCard padding={16}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
                      <AvatarCircle firstName={owner.assigneeAccount.firstName || ''} lastName={owner.assigneeAccount.lastName || ''} size={40} style={{ marginRight: 12 }} />
                      <View style={{ flex: 1 }}>
                        <Text style={{ fontSize: 16, fontWeight: '600', color: redesign.text.primary, marginBottom: 2 }}>{nameString}</Text>
                        <Text style={{ fontSize: 14, color: redesign.text.muted }}>Admin</Text>
                      </View>
                    </View>
                    {owner.assignedAt && (
                      <View style={{ paddingLeft: 52 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                          <SVG_COMPONENTS.CALENDAR width={16} height={16} color={redesign.text.muted} />
                          <Text style={{ fontSize: 14, fontWeight: '500', color: redesign.text.primary, marginLeft: 8 }}>{new Date(owner.assignedAt).toLocaleString()}</Text>
                        </View>
                        <Text style={{ fontSize: 12, color: redesign.text.subtle, lineHeight: 16 }}>
                          {nameString} assigned to this thread by {owner.assignedBy?.firstName ? `${owner.assignedBy.firstName} ${owner.assignedBy.lastName}` : owner.assignedBy?.email || 'System'}
                        </Text>
                      </View>
                    )}
                  </GlassCard>
                </View>
              );
            })
          )}
        </View>
      </ScrollView>
      {/* Resident details side panel */}
      <ResidentDetailsSidePanel
        visible={showResidentPanel}
        onClose={() => setShowResidentPanel(false)}
        accountId={residentParams.accountId}
        leaseId={residentParams.leaseId}
        mode={'account'}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
